import { anrufUebergeben } from './anruf/klingeln.js'
import { api, ApiError } from './api.js'
import type { StartAktionen } from './routes/Start.js'

/**
 * Die Direktaktionen der Startseite (Anf. 77, Szenarien 1, 2 und 6).
 *
 * Hier steckt die Arbeit, die den Unterschied zwischen zwei und vier Tipps
 * ausmacht: **Der Raum wird beschafft, nicht erfragt.** Gibt es noch keinen
 * Direkt-Chat mit dieser Person, legt der Client ihn im selben Zug an. Der
 * Nutzer merkt davon nichts — für ihn hat er einmal auf „Nachricht" getippt.
 *
 * Ausgelagert aus `Start.tsx`, damit die Oberfläche testbar bleibt, ohne dass
 * ein Test das Netz braucht: Die Komponente bekommt die Aktionen übergeben.
 */
export function direktAktionen(): StartAktionen {
  /**
   * Liefert den Direkt-Raum mit dieser Person — vorhanden oder neu.
   *
   * `POST /rooms/direct` ist absichtlich idempotent (Phase 4): Zwei parallele
   * Aufrufe erzeugen keinen zweiten Raum, sondern liefern denselben.
   */
  async function raumFuer(personId: string, bekannt: string | null): Promise<string> {
    if (bekannt) return bekannt
    const res = await api.post<{ roomId: string }>('/rooms/direct', { personId })
    return res.roomId
  }

  return {
    onChat(personId, roomId) {
      void raumFuer(personId, roomId).then((id) => {
        location.hash = `#chat/${id}`
      })
    },

    /**
     * Anrufen — und dabei den Anruf auch übergeben.
     *
     * Hier stand: anlegen, Antwort wegwerfen, `location.hash = '#anrufen'`.
     * Der Anruf entstand also wirklich, beim Angerufenen klingelte es — und
     * der **Anrufer** landete auf der Anrufliste statt im Gespräch. Kein
     * „Es klingelt…", kein Mesh, keine Verbindung. Wer von der Familienseite
     * aus anrief, rief in einen Raum, den er selbst nie betrat.
     *
     * Läuft im Raum schon ein Gespräch, antwortet der Server mit 409 und der
     * ID des laufenden. Dann wird beigetreten statt gemeldet — dieselbe Regel
     * wie auf dem Anrufbildschirm.
     */
    onAnruf(personId, roomId, kind) {
      void raumFuer(personId, roomId)
        .then(async (id) => {
          try {
            return await api.post<unknown>('/calls', { roomId: id, kind })
          } catch (e) {
            const laufend = e instanceof ApiError && e.code === 'conflict'
              ? e.data?.callId
              : undefined
            if (typeof laufend !== 'string') throw e
            return await api.post<unknown>(`/calls/${laufend}/join`, {})
          }
        })
        .then((call) => {
          anrufUebergeben(call)
          location.hash = '#anrufen'
        })
        .catch(() => {
          // Der Anruf kam nicht zustande. Den Bereich trotzdem öffnen: Dort
          // steht die Liste mit einem Knopf, statt hier still nichts zu tun.
          location.hash = '#anrufen'
        })
    },

    onFoto(personId, roomId) {
      // Der Chat übernimmt das Auswählen und Verschlüsseln. Der Umweg ist
      // Absicht: Ein Foto zu senden heißt, eine Nachricht zu senden — nur mit
      // Bild statt Text. Ein zweiter Weg dafür wäre ein zweiter Ort, an dem
      // die Verschlüsselung richtig sein muss.
      void raumFuer(personId, roomId).then((id) => {
        location.hash = `#chat/${id}?foto=1`
      })
    },
  }
}
